import React, { useEffect, useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Emblem } from "@/components/Emblem";

const KEY = "mib_gate_unlocked";
// Password gate is DISABLED during the testing/review phase.
// Flip this to true (and re-enable in the launch checklist) to lock the site.
const GATE_ENABLED = false;

export default function SiteGate({ children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setUnlocked(localStorage.getItem(KEY) === "1");
    setReady(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/gate/verify", { password });
      localStorage.setItem(KEY, "1");
      setUnlocked(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "That password does not open the gate.");
    } finally {
      setBusy(false);
    }
  };

  if (!GATE_ENABLED) return children;
  if (!ready) return null;
  if (unlocked) return children;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F1E7] px-6" data-testid="site-gate">
      <div className="w-full max-w-sm text-center rise-in">
        <div className="flex justify-center mb-8">
          <Emblem size={96} />
        </div>
        <h1 className="font-serif font-bold text-2xl sm:text-3xl tracking-wide text-[#003366]">Made in Boston</h1>
        <p className="font-playfair italic text-base text-[#7a611c] mt-3">Two harbours. One name.</p>
        <p className="font-sans text-sm text-[#505A68] mt-6 leading-relaxed">
          This crossing is not yet open to the public. Enter the password to come aboard.
        </p>

        <form onSubmit={submit} className="mt-8" data-testid="gate-form">
          <div className="flex items-stretch border border-[#D6D3CB] bg-[#FFFAF3] focus-within:border-[#003366] transition-colors">
            <input
              type="password"
              autoFocus
              data-testid="gate-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="flex-1 bg-transparent px-4 py-3 text-sm font-sans text-[#1B2430] outline-none"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="gate-submit"
              aria-label="Enter"
              className="bg-[#1E1E2F] hover:bg-[#003366] text-[#FFFAF3] px-5 flex items-center transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
            </button>
          </div>
          {error && (
            <p data-testid="gate-error" className="font-sans text-sm text-[#003366] mt-3">{error}</p>
          )}
        </form>

        <div className="mt-14 flex items-center gap-3 justify-center">
          <div className="w-8 h-px bg-[#CDA434]" />
          <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#505A68]">Made in Boston</span>
          <div className="w-8 h-px bg-[#CDA434]" />
        </div>
      </div>
    </div>
  );
}
